import { useCallback, useMemo, useState } from 'react';
import { produce } from 'immer';
import { Button } from '@antimatterhq/ui/components/button';
import Spacer from '@antimatterhq/ui/components/spacer';
import {
  SimpleTable,
  SimpleTableHeader,
  SimpleTableRow,
  SimpleTableHead,
  SimpleTableBody,
  SimpleTableCell,
  SimpleTableSkeletonRows,
} from '@antimatterhq/ui/components/simpleTable';
import { useUpdateWorkspaceConfig } from '@/hooks/workspace/useUpdateWorkspaceConfig';
import {
  useGetWorkspaceConfig,
  useOptimisticUpdateWorkspaceConfig,
} from '@/hooks/workspace/useGetWorkspaceConfig';
import { ObservableRelationshipDialog } from '@/components/dialog';
import { bumpMetadataVersion } from '@/utils/util';

export const ObservableRelationships = () => {
  const { data, isPending } = useGetWorkspaceConfig();
  const { mutateAsync: updateConfig } = useUpdateWorkspaceConfig();
  const [activeRelationship, setActiveRelationship] = useState<
    string | undefined
  >(undefined);
  const optimisticUpdate = useOptimisticUpdateWorkspaceConfig();
  const [editIndex, setEditIndex] = useState<number | undefined>(undefined);

  const relationships = useMemo(() => {
    return data?.spec.observables?.relationships || [];
  }, [data]);

  const onSubmit = useCallback(
    async (updatedRelationships: string[]) => {
      if (!data) {
        return;
      }

      const config = produce(data, (draft) => {
        if (!draft.spec.observables) {
          draft.spec.observables = { relationships: [] };
        }
        draft.spec.observables.relationships = [...updatedRelationships];
        bumpMetadataVersion(draft);
      });

      // optimistic update of local data
      optimisticUpdate(config);

      // save to API
      await updateConfig(config, {
        toasts: {
          success: 'Observable relationships updated',
          error: 'Failed to update observable relationships',
        },
      });
    },
    [data, updateConfig, optimisticUpdate],
  );

  const addObservable = useCallback(() => {
    if (!activeRelationship) {
      return;
    }
    const updatedRelationships = [...relationships, activeRelationship];
    onSubmit(updatedRelationships);
  }, [activeRelationship, onSubmit, relationships]);

  const updateObservable = useCallback(() => {
    if (!activeRelationship) {
      return;
    }

    const updatedRelationships = relationships.map((o, index) =>
      index === editIndex ? activeRelationship : o,
    );
    onSubmit(updatedRelationships);

    setActiveRelationship(undefined);
    setEditIndex(undefined);
  }, [activeRelationship, editIndex, onSubmit, relationships]);

  const removeObservable = useCallback(
    (relationship: string) => {
      const updatedRelationships = relationships.filter(
        (o) => o !== relationship,
      );
      onSubmit(updatedRelationships);
    },
    [relationships, onSubmit],
  );

  const onSubmitObservable = useCallback(async () => {
    if (editIndex !== undefined) {
      updateObservable();
    } else {
      addObservable();
    }
    setActiveRelationship(undefined);
  }, [addObservable, updateObservable, editIndex]);

  return (
    <>
      <SimpleTable className="w-full" fixed>
        <SimpleTableHeader>
          <SimpleTableRow hover={false}>
            <SimpleTableHead className="w-[250px] font-bold">
              Observable relationship
            </SimpleTableHead>
          </SimpleTableRow>
        </SimpleTableHeader>
        <SimpleTableBody>
          {!isPending &&
            relationships.map((relationship, index) => (
              <SimpleTableRow key={relationship} className="h-[31px]">
                <SimpleTableCell
                  className="relative"
                  data-testid={`relationship-${relationship}`}
                >
                  {relationship}
                  <div className="invisible absolute right-0 top-0 flex gap-1">
                    <Button
                      iconOnly
                      leftIcon="antimatterhq:pencil"
                      size="small"
                      variant="ghost"
                      className="p-0"
                      onClick={() => {
                        setActiveRelationship(relationship);
                        setEditIndex(index);
                      }}
                      data-testid={`edit-relationship-${relationship}-button`}
                    />
                    <Button
                      iconOnly
                      leftIcon="antimatterhq:trash"
                      size="small"
                      variant="ghost"
                      className="p-0"
                      onClick={() => removeObservable(relationship)}
                    />
                  </div>
                </SimpleTableCell>
              </SimpleTableRow>
            ))}
          {isPending && (
            <SimpleTableSkeletonRows cols={1} height={31} rows={5} />
          )}
        </SimpleTableBody>
      </SimpleTable>

      <Spacer size="lg" />

      <div className="flex justify-end gap-2">
        <Button
          variant="default"
          leftIcon="radix-icons:plus"
          onClick={() => setActiveRelationship('')}
          data-testid="add-relationship-button"
        >
          Add a relationship
        </Button>
      </div>
      <ObservableRelationshipDialog
        open={activeRelationship !== undefined}
        onOpenChange={(open) => {
          if (!open) {
            setActiveRelationship(undefined);
          }
        }}
        onSave={onSubmitObservable}
        relationship={activeRelationship}
        setRelationship={setActiveRelationship}
        isEdit={editIndex !== undefined}
        existingRelationships={relationships}
      />
    </>
  );
};
